import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import type { ItemType, PageId } from "core/entities";
import { useBreakpoints } from "core/hooks/useBreakpoints";
import { useToaster } from "core/hooks/useToaster";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFavorite } from "../hooks/useFavorite";

export interface FavoriteButtonProps<T extends ItemType> {
  itemType: T;
  itemId: T extends "Page" ? PageId : string;
  title: string;
  color?: "primary" | "inherit";
}

export function FavoriteButton<T extends ItemType>({
  itemType,
  itemId,
  title,
  color = "primary",
}: FavoriteButtonProps<T>) {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:FavoriteButton",
  });
  const timeoutRef = useRef<null | ReturnType<typeof setTimeout>>(null);
  const { isMobile } = useBreakpoints();
  useEffect(
    () => () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );
  const { successToast, errorToast } = useToaster();
  const { isFavorite, addToFavorites, removeFromFavorites } = useFavorite(
    itemType,
    itemId,
    title
  );
  const [tempMessage, setTempMessage] = useState<null | string>(null);
  const toastMessage = isFavorite ? t("toastRemove") : t("toastAdd");
  const tooltipTitle = isFavorite ? t("hoverRemove") : t("hoverAdd");

  async function handleClick() {
    try {
      if (!isFavorite) {
        setTempMessage(toastMessage);
        timeoutRef.current = setTimeout(() => {
          setTempMessage(null);
        }, 3000);
        await addToFavorites();
      } else {
        setTempMessage(toastMessage);
        timeoutRef.current = setTimeout(() => {
          setTempMessage(null);
        }, 3000);
        await removeFromFavorites();
      }
      successToast(toastMessage);
    } catch {
      errorToast(t("toastError"));
    }
  }
  const size = isMobile ? "medium" : "large";
  return (
    <Tooltip title={tempMessage ?? tooltipTitle}>
      <IconButton
        sx={{ zIndex: 1 }}
        size={size}
        onClick={handleClick}
        color={color}
      >
        {isFavorite ? (
          <StarIcon fontSize={size} />
        ) : (
          <StarBorderIcon fontSize={size} />
        )}
      </IconButton>
    </Tooltip>
  );
}
